import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import css from './file-upload-modal.module.css';
import { AddImageIcon, DeleteIcon } from './icons';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../components/common/spinner';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { SystemConfiguration } from '../../../enums/system-configuration';
import { Line } from 'rc-progress';
import { convertFileSizeToMb } from '../utils/file-helper';

interface FileWrapper {
  file: File;
  preview: string;
  name: string;
  size: number;
  type: string;
  uploaded: boolean;
}

interface UploadProgress {
  [key: string]: number;
}

interface FileUploadModalProps {
  onClose: () => void;
  handleUploadFiles: (files: File[]) => void;
}

const generateAcceptObject = (fileTypes: string[]) => {
  const mimeTypes: { [key: string]: string[] } = {
    jpg: ['image/jpeg'],
    jpeg: ['image/jpeg'],
    png: ['image/png'],
    pdf: ['application/pdf'],
    xls: ['application/vnd.ms-excel'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    csv: ['text/csv'],
    zip: ['application/zip'],
    doc: ['application/msword'],
    docx: [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  };

  const acceptObject: { [key: string]: string[] } = {};

  fileTypes.forEach((fileType) => {
    const lowerFileType = fileType.toLowerCase();
    if (mimeTypes[lowerFileType]) {
      mimeTypes[lowerFileType].forEach((mimeType) => {
        if (!acceptObject[mimeType]) {
          acceptObject[mimeType] = [];
        }
        acceptObject[mimeType].push(`.${lowerFileType}`);
      });
    }
  });

  return acceptObject;
};

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  onClose,
  handleUploadFiles,
}) => {
  const { t } = useTranslation();
  const { uploadFileTypes, uploadFileMaxSize } = useSelector(
    (state: RootState) => ({
      uploadFileTypes:
        state.global.systemConfiguration[
          SystemConfiguration.UPLOAD_FILE_EXTENSIONS
        ].split(';'),
      uploadFileMaxSize: parseInt(
        state.global.systemConfiguration[
          SystemConfiguration.UPLOAD_FILE_MAX_SIZE
        ]
      ),
    })
  );

  const [files, setSelectedFiles] = useState<FileWrapper[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const onDrop = (acceptedFiles: File[]) => {
    const newFiles: FileWrapper[] = acceptedFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: convertFileSizeToMb(file.size, 2),
      type: file.type,
      uploaded: false,
    }));

    const oversizedFiles = newFiles.filter(
      (file) => file.size > uploadFileMaxSize
    );
    if (oversizedFiles.length > 0) {
      setErrorMessage(
        t('pages.document_storage.file_size_error', {
          size: `${uploadFileMaxSize}MB`,
        })
      );
      return;
    }

    setErrorMessage(null);
    setSelectedFiles([...files, ...newFiles]);
    uploadFiles(newFiles);
  };

  const uploadFiles = (files: FileWrapper[]) => {
    files.forEach((fileWrapper) => {
      const file = fileWrapper.file;
      const reader = new FileReader();
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [file.name]: (e.loaded / e.total) * 100,
          }));
        }
      };
      reader.onloadend = () => {
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [file.name]: 100,
        }));
        setSelectedFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.name === file.name ? { ...f, uploaded: true } : f
          )
        );
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: generateAcceptObject(uploadFileTypes),
  });

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const filesToUpload = files.map((fileWrapper) => fileWrapper.file);

      await handleUploadFiles(filesToUpload);

      onClose();
    } catch (err) {
      console.error('Error uploading files:', err);
      console.error(JSON.stringify(err, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  const removeFile = (fileName: string) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file) => file.name !== fileName)
    );
    setUploadProgress((prevProgress) => {
      const { [fileName]: _, ...rest } = prevProgress;
      return rest;
    });
  };

  const fileNames = uploadFileTypes.join(', ');

  return (
    <div className={css['container']}>
      <p className={css['title']}>
        {t('pages.document_storage.file_types')}
        {fileNames}
      </p>
      <div {...getRootProps({ className: css['dropzone'] })}>
        <input {...getInputProps()} />
        <img src={AddImageIcon} alt="upload" />
        <div>
          <span className="text-link font-bold mr-1">
            {t('pages.document_storage.upload_file')}
          </span>
          <span className="text-[#4B5563]">
            {t('pages.document_storage.or_drag_here')}
          </span>
        </div>
      </div>
      {errorMessage && <p className={css['error-message']}>{errorMessage}</p>}
      <div>
        {files.map((fileWrapper) => (
          <div key={fileWrapper.name} className={css['file-item']}>
            <div className="w-full flex justify-between">
              <p className="w-[295px] truncate text-start text-link p-0">
                {fileWrapper.name}
              </p>
              {fileWrapper.uploaded && (
                <div className="flex gap-1">
                  <p className="text-gray-2 p-0">{fileWrapper.size} MB</p>
                  <button
                    onClick={() => removeFile(fileWrapper.name)}
                    className="delete-button"
                    aria-label={t('delete_file')}
                  >
                    <img src={DeleteIcon} alt="delete" />
                  </button>
                </div>
              )}
            </div>
            {fileWrapper.uploaded && (
              <div className="w-full">
                <Line
                  percent={uploadProgress[fileWrapper.name] ?? 0}
                  strokeWidth={1}
                  strokeColor="#002a58"
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={css['buttons']}>
        <button onClick={onSubmit} disabled={files.length === 0}>
          <Spinner visible={isLoading} size="sm" className="mr-2" />
          {t('add')}
        </button>
        <button onClick={onClose}>{t('cancel')}</button>
      </div>
    </div>
  );
};

export default FileUploadModal;
